/**
 * VAT Check Button Component
 * Button for triggering VAT validation checks
 */

import React, { useState } from 'react';
import { Button, Spinner, Alert, Modal } from 'react-bootstrap';
import { vatValidationService } from '../services/vatValidationService';
import { BulkVatValidationResult } from '../services/vatValidationService';

interface VatCheckButtonProps {
  type: 'single' | 'bulk';
  targetId?: string | number; // customerId or contractId for single check
  targetIds?: string[]; // contractIds for bulk check
  onResults?: (results: any) => void;
  buttonText?: string;
  variant?: string;
  size?: 'sm' | 'lg';
}

export const VatCheckButton: React.FC<VatCheckButtonProps> = ({
  type = 'single',
  targetId,
  targetIds = [],
  onResults,
  buttonText = 'Check VAT',
  variant = 'primary',
  size
}) => {
  const [loading, setLoading] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [validationResults, setValidationResults] = useState<BulkVatValidationResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleVatCheck = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let results;
      
      if (type === 'single' && targetId) {
        if (typeof targetId === 'number') {
          // Customer ID
          const result = await vatValidationService.validateCustomerVat(targetId);
          results = [{
            customer_id: targetId,
            contract_id: null,
            ...result
          }];
        } else {
          // Contract ID
          const bulkResult = await vatValidationService.checkContractLinesVat([targetId.toString()]);
          results = bulkResult.results;
        }
      } else if (type === 'bulk' && targetIds.length > 0) {
        // Bulk check for contracts
        const bulkResult = await vatValidationService.checkContractLinesVat(targetIds);
        results = bulkResult.results;
      } else {
        throw new Error('Invalid target for VAT check');
      }
      
      setValidationResults(results);
      setShowResultsModal(true);
      
      if (onResults) {
        onResults(results);
      }
      
    } catch (err) {
      console.error('VAT check failed:', err);
      setError(err instanceof Error ? err.message : 'VAT check failed');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (result: BulkVatValidationResult) => {
    if (!result.checked) {
      return <span className="badge bg-warning text-dark">Not Checked</span>;
    } else if (result.is_valid) {
      return <span className="badge bg-success">Valid</span>;
    } else {
      return <span className="badge bg-danger">Invalid</span>;
    }
  };

  const getCountryFlag = (countryCode: string) => {
    // Simple country flag emoji based on country code
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleVatCheck}
        disabled={loading || (type === 'single' && !targetId) || (type === 'bulk' && targetIds.length === 0)}
      >
        {loading ? (
          <>
            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
            {' Checking...'}
          </>
        ) : (
          buttonText
        )}
      </Button>

      {/* Results Modal */}
      <Modal show={showResultsModal} onHide={() => setShowResultsModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>VAT Validation Results</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {validationResults.length === 0 ? (
            <p>No validation results available.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>Customer/Contract</th>
                    <th>Country</th>
                    <th>VAT Number</th>
                    <th>Status</th>
                    <th>Tax Rate</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {validationResults.map((result, index) => (
                    <tr key={index}>
                      <td>
                        {result.customer_id ? `Customer #${result.customer_id}` : ''}
                        {result.contract_id ? `Contract #${result.contract_id}` : ''}
                      </td>
                      <td>
                        {getCountryFlag(result.country_code)} {result.customer_country}
                      </td>
                      <td>{result.vat_number_normalized || 'N/A'}</td>
                      <td>{getStatusBadge(result)}</td>
                      <td>
                        <span className="badge bg-info">
                          {result.tax_rate}%
                        </span>
                      </td>
                      <td>
                        {result.failure_reason && (
                          <small className="text-muted" title={result.failure_reason}>
                            {result.failure_reason.length > 30 
                              ? result.failure_reason.substring(0, 30) + '...' 
                              : result.failure_reason}
                          </small>
                        )}
                        {result.request_identifier && (
                          <div className="mt-1">
                            <small className="text-secondary">
                              Request ID: {result.request_identifier}
                            </small>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResultsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mt-2" onClose={() => setError(null)} dismissible>
          <Alert.Heading>VAT Check Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}
    </>
  );
};

export default VatCheckButton;
